import { Option, Select } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useUpdatePatientCategory } from '../../../../../api/generated/anamnesis-controller/anamnesis-controller';
import { PatientCategory as PatientCategorytype } from '../../../../../api/generated/model';
import { getGetAnamnesesByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller';
import { SingleLineFormControl } from '../../../../../components/form/SingeLineFormComponents';

const PatientCategory = {
  NEUE_ERKRANKUNG: 'NEUE_ERKRANKUNG',
  RUECKFALL: 'RUECKFALL',
  THERAPIEVERSAGER: 'THERAPIEVERSAGER',
  THERAPIEABBRUCH: 'THERAPIEABBRUCH',
  CHRONISCHE_TUBERKULOSE: 'CHRONISCHE_TUBERKULOSE',
  FORTFUEHRUNG_NACH_VERLEGUNG: 'FORTFUEHRUNG_NACH_VERLEGUNG',
  UNBEKANNT: 'UNBEKANNT',
} as const;

function getPatientCategoryName(value: string) {
  switch (value) {
    case PatientCategory.NEUE_ERKRANKUNG:
      return 'Neue Erkrankung (noch nie behandelt)';
    case PatientCategory.RUECKFALL:
      return 'Rückfall (vollständig behandelt/geheilt, später wieder positiv)';
    case PatientCategory.THERAPIEVERSAGER:
      return 'Therapieversagen (nach 5 Behandlungsmonaten immer noch oder wieder positiv)';
    case PatientCategory.THERAPIEABBRUCH:
      return 'Therapieabbruch (Therapieabbruch oder Therapieunterbruch von mindestens 2 Monaten)';
    case PatientCategory.CHRONISCHE_TUBERKULOSE:
      return 'chronische Tuberkulose (Rückfall nach zwei vollständigen Behandlungen)';
    case PatientCategory.FORTFUEHRUNG_NACH_VERLEGUNG:
      return 'Fortführung der Therapie nach Verlegung (aus anderem Kanton oder Land)';
    case PatientCategory.UNBEKANNT:
      return 'unbekannt';
    default:
      return value;
  }
}

export function EditPatientCategoryForm({
  patient,
  patientId,
  anamnesisId,
}: {
  patientId: string;
  selectedAnamnesisType: string;
  patient?: PatientCategorytype | null;
  anamnesisId: string;
}) {
  const { control, getFieldState } = useForm({
    defaultValues: {
      patientCategory: patient ?? PatientCategory.UNBEKANNT,
    },
  });

  const queryClient = useQueryClient();

  const { mutate } = useUpdatePatientCategory({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAnamnesesByPatientQueryKey(patientId),
        });
      },
    },
  });

  return (
    <SingleLineFormControl label="Patientenkategorie" fieldState={getFieldState('patientCategory')}>
      <Controller
        name="patientCategory"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            value={value}
            onChange={(_, newValue) => {
              if (!newValue || newValue === value) return;

              onChange(newValue);

              mutate({
                id: anamnesisId,
                data: {
                  patientCategory: newValue,
                },
              });
            }}
            variant="plain">
            {Object.values(PatientCategory).map((category) => (
              <Option key={category} value={category}>
                {getPatientCategoryName(category)}
              </Option>
            ))}
          </Select>
        )}
      />
    </SingleLineFormControl>
  );
}
